import { useComponentStatesStore } from '@stores/componentStatesStore'
import { useConfigurationStore } from '@stores/configurationStore'
import { useSpinButtonSectionStore } from '@stores/spinButtonSectionStore'
import { GameType, ViewportType } from 'game-ui-types'

// Assumption: There can be maximum a promo widget button and another button (amuseBoost/buyBonus/doubleChance) at a time
export const useAmuseBoostPromoWidgetPositions = () => {
    // TODO: remove when promo removed from platform
    const hasWidgetInUI = useConfigurationStore((state) => state.hasWidgetInUI)
    const viewport = useConfigurationStore((state) => state.viewport)
    const gameType = useConfigurationStore((state) => state.gameType)
    const isLeftHandMode = useSpinButtonSectionStore((state) => state.isLeftHandMode)
    const computedReelSizes = useConfigurationStore((state) => state.computedReelSizes)
    const reelSizes = computedReelSizes()[viewport]

    const currentAvailablePromoWidgets = useComponentStatesStore((state) => state.currentAvailablePromoWidgets)
    const shouldUseAmuseBoost = useConfigurationStore((state) => state.shouldUseAmuseBoost)
    const hasDoubleChanceFeature = useConfigurationStore((state) => state.hasDoubleChance)
    const hasBuyBonusFeature = useConfigurationStore((state) => state.hasBuyBonus)

    const hasPromoWidget = currentAvailablePromoWidgets.length >= 1
    const hasAmuseBoost = shouldUseAmuseBoost && hasBuyBonusFeature && hasDoubleChanceFeature
    const hasBuyBonus = (!shouldUseAmuseBoost || !hasDoubleChanceFeature) && hasBuyBonusFeature
    const hasDoubleChance = !shouldUseAmuseBoost && hasDoubleChanceFeature

    const promoWidgetConfigDimensions = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').dimensions)
    const promoWidgetConfigPosition = useConfigurationStore((s) => s.getComponentConfig('PromoWidget').position)

    const amuseBoostButtonConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('AmuseBoostButton').position
    )
    const buyBonusButtonConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('BuyBonusButton').position
    )
    const doubleChanceButtonConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('DoubleChanceButton').position
    )
    const doubleChanceButtonNoAmuseBoostConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('DoubleChanceButtonNoAmuseBoost').position
    )

    const amuseBoostButtonConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('AmuseBoostButton').dimensions
    )
    const buyBonusButtonConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('BuyBonusButton').dimensions
    )
    const doubleChanceButtonConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('DoubleChanceButton').dimensions
    )
    const doubleChanceButtonNoAmuseBoostConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('DoubleChanceButtonNoAmuseBoost').dimensions
    )

    const secondaryButtonConfigPosition = hasAmuseBoost
        ? amuseBoostButtonConfigPosition
        : hasBuyBonus
          ? buyBonusButtonConfigPosition
          : shouldUseAmuseBoost
            ? doubleChanceButtonConfigPosition
            : doubleChanceButtonNoAmuseBoostConfigPosition

    const secondaryButtonConfigDimensions = hasAmuseBoost
        ? amuseBoostButtonConfigDimensions
        : hasBuyBonus
          ? buyBonusButtonConfigDimensions
          : shouldUseAmuseBoost
            ? doubleChanceButtonConfigDimensions
            : doubleChanceButtonNoAmuseBoostConfigDimensions

    // TODO: while widget exists in platform follow config positions
    if (!hasWidgetInUI || viewport === ViewportType.MOBILE_PORTRAIT) {
        return {
            promoWidgetPosition: promoWidgetConfigPosition,
            secondaryButtonPosition: secondaryButtonConfigPosition,
        }
    }

    // Usually these buttons show on the opposite side of the spin button section except for Config D and Config E
    let direction: 'left' | 'right' = isLeftHandMode ? 'right' : 'left'

    if (gameType === GameType.SLOT_CONFIG_D || gameType === GameType.SLOT_CONFIG_E) {
        direction = isLeftHandMode ? 'left' : 'right'
    }

    const remainingSpace = reelSizes.x
    const availableButtonsCount = [hasAmuseBoost, hasBuyBonus, hasDoubleChance, hasPromoWidget].filter(Boolean).length

    const promoWidgetWidth = parseInt(promoWidgetConfigDimensions?.width ?? '0', 10)
    const secondaryButtonWidth = parseInt(secondaryButtonConfigDimensions?.width ?? '0', 10)

    const defaultPromoWidgetX = parseInt(promoWidgetConfigPosition?.left ?? promoWidgetConfigPosition?.right ?? '0', 10)
    const defaultSecondaryButtonX = parseInt(
        secondaryButtonConfigPosition?.left ?? secondaryButtonConfigPosition?.right ?? '0'
    )

    let promoWidgetPosition = defaultPromoWidgetX
    let secondaryButtonPosition = defaultSecondaryButtonX

    if (availableButtonsCount) {
        if (gameType === GameType.SLOT_CONFIG_A || gameType === GameType.SLOT_CONFIG_F || availableButtonsCount === 1) {
            // Config A: Buttons are underneath eachother so the x is the same for both
            // Config F: EQ Games only could have Promo Widget
            // If has one button center it

            promoWidgetPosition = Math.max(remainingSpace / 2 - promoWidgetWidth / 2, 0)
            secondaryButtonPosition = Math.max(remainingSpace / 2 - secondaryButtonWidth / 2, 0)
        } else {
            // Else if more buttons and other configs: the promo widget is on the left and the secondary button on the right in that order always within the section
            // As a section they can appear on the left or right of the screen/stage depending on the config and if isLeftHandMode enabled

            const padding = defaultSecondaryButtonX - defaultPromoWidgetX - promoWidgetWidth
            const sectionWidth = promoWidgetWidth + padding + secondaryButtonWidth
            let leftButtonX = remainingSpace / 2 - sectionWidth / 2
            let rightButtonX = leftButtonX + padding + promoWidgetWidth

            if (isLeftHandMode) {
                const tempLeft = leftButtonX

                leftButtonX = rightButtonX
                rightButtonX = tempLeft
            }

            if (direction === 'left' && leftButtonX < 0) {
                leftButtonX = 0
                rightButtonX = Math.abs(promoWidgetWidth + padding)
            } else if (direction === 'right' && rightButtonX < 0) {
                rightButtonX = 0
                leftButtonX = Math.abs(promoWidgetWidth + padding)
            }

            promoWidgetPosition = leftButtonX
            secondaryButtonPosition = rightButtonX
        }
    }

    // Since in some configs, promo is above buy bonus, it would not make sense to have it in the middle
    // of the screen on its own, so move it to the bottom when there is no buy bonus feature
    const promoWidgetY =
        hasAmuseBoost || hasBuyBonus || hasDoubleChance ? promoWidgetConfigPosition : buyBonusButtonConfigPosition

    return {
        promoWidgetPosition: {
            bottom: promoWidgetY?.bottom,
            top: promoWidgetY?.top,
            [direction]: `${promoWidgetPosition}px`,
        },
        secondaryButtonPosition: {
            bottom: secondaryButtonConfigPosition?.bottom,
            top: secondaryButtonConfigPosition?.top,
            [direction]: `${secondaryButtonPosition}px`,
        },
    }
}
